import { memo } from 'react'
import { frameIsStyled, type BoardTheme, type Frame } from '@praesto/shared'
import type { CameraStore } from './CameraStore.js'
import type { ResizeCorner } from './ElementView.js'
import { useText } from '../i18n.js'
import { useIsVisible } from './useAdaptiveImage.js'
import { FrameOptions } from './FrameOptions.js'

const CORNERS: ResizeCorner[] = ['nw', 'ne', 'se', 'sw']
const EDGES = ['n', 'e', 's', 'w'] as const

export interface FrameViewProps {
  frame: Frame
  /** Position in der Präsentationsreihenfolge, 1-basiert. */
  position: number
  camera: CameraStore
  selected: boolean
  /** Einziges ausgewähltes Objekt — nur dann gibt es Anfasser und Gestaltungsleiste. */
  sole: boolean
  /** Werkzeuge zeigen? Im Vortrag und bei Gästen nicht. */
  showChrome: boolean
  onSelect: (id: string, additive: boolean) => void
  onZoomTo: (frame: Frame) => void
  onDragStart: (event: React.PointerEvent, frame: Frame) => void
  onResizeStart: (event: React.PointerEvent, frame: Frame, corner: ResizeCorner) => void
  onPatch: (id: string, patch: Partial<Frame>) => void
  onSaveTemplate?: (frame: Frame) => void
  /** Nimmt dieser Frame beim Größenändern seinen Inhalt mit? */
  scaleContent: boolean
  onToggleScaleContent?: () => void
  theme: BoardTheme
}

/**
 * Ein Frame ist ein benannter Ausschnitt des Boards — und, sobald er
 * Hintergrund oder Rahmen bekommt, auch ein Gestaltungselement.
 *
 * Deshalb die Trennung in zwei Schichten: Die **Füllung** ist Inhalt und wird
 * jedem gezeigt, auch Gästen im Vortrag. Die **Werkzeugschicht** — dünner
 * Strukturstrich, Beschriftung, Kanten, Anfasser — sieht nur, wer bearbeiten
 * darf.
 *
 * Angefasst wird der Frame an der Beschriftung und an seinen vier Kanten. Die
 * Kanten liegen bewusst *außerhalb* der Fläche: sonst nähmen sie Elementen
 * direkt am Rand die Klicks weg.
 */
export const FrameView = memo(function FrameView({
  frame,
  position,
  camera,
  selected,
  sole,
  showChrome,
  onSelect,
  onZoomTo,
  onDragStart,
  onResizeStart,
  onPatch,
  onSaveTemplate,
  scaleContent,
  onToggleScaleContent,
  theme,
}: FrameViewProps): React.ReactElement | null {
  const t = useText()
  const visible = useIsVisible(camera, frame)
  const styled = frameIsStyled(frame)

  if (!visible && !selected) return null
  // Ohne Gestaltung und ohne Werkzeuge gibt es nichts zu zeichnen.
  if (!showChrome && !styled) return null

  const grab = (event: React.PointerEvent): void => {
    if (!showChrome) return
    event.stopPropagation()
    // Umschalt nimmt den Frame zur Auswahl hinzu, statt sie zu ersetzen — und
    // verschiebt dann nichts: Wer gerade auswählt, will nicht zugleich bewegen.
    if (event.shiftKey) {
      onSelect(frame.id, true)
      return
    }
    onSelect(frame.id, false)
    onDragStart(event, frame)
  }

  return (
    <div
      className={`frame${selected ? ' is-selected' : ''}${showChrome ? ' has-chrome' : ''}`}
      style={{
        left: `${frame.x}px`,
        top: `${frame.y}px`,
        width: `${frame.w}px`,
        height: `${frame.h}px`,
      }}
      data-frame-id={frame.id}
    >
      <div
        className="frame-fill"
        aria-hidden="true"
        style={{
          backgroundColor: frame.background || 'transparent',
          border:
            frame.borderWidth > 0
              ? `${frame.borderWidth}px solid ${frame.borderColor}`
              : undefined,
        }}
      />

      {showChrome && (
        <>
          {/* Dünner Strukturstrich, damit ein ungestalteter Frame überhaupt
              sichtbar ist. Bei gestalteten Frames wäre er nur Störung. */}
          {!styled && <div className="frame-outline" aria-hidden="true" />}

          {EDGES.map((edge) => (
            <div
              key={edge}
              className={`frame-edge frame-edge-${edge}`}
              onPointerDown={grab}
              title={t('frame.dragTitle')}
            />
          ))}

          <button
            type="button"
            className="frame-label"
            onPointerDown={grab}
            onDoubleClick={(event) => {
              event.stopPropagation()
              onZoomTo(frame)
            }}
            title={t('frame.labelTitle')}
          >
            <span className="frame-number">{position}</span>
            {frame.name}
          </button>

          {sole && (
            <>
              <FrameOptions
                frame={frame}
                theme={theme}
                camera={camera}
                onPatch={onPatch}
                onSaveTemplate={onSaveTemplate}
                scaleContent={scaleContent}
                onToggleScaleContent={onToggleScaleContent}
              />
              {CORNERS.map((corner) => (
                <div
                  key={corner}
                  className={`handle handle-${corner}`}
                  onPointerDown={(event) => {
                    event.stopPropagation()
                    onResizeStart(event, frame, corner)
                  }}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  )
})
